import { TEMP_ID_PREFIX } from './constants';
import type { RouteMatcher } from './routes';
import type { OfflineStorage } from './storage';
import type { EntityType, SyncQueueItem, SyncStatus } from './types';
import { generateTempId, nowIso } from './timestamps';

function clientIdFromUrl(url: string): string | undefined {
  const match = url.match(/^\/clients\/([^/]+)/);
  return match?.[1];
}

function entityIdFromUrl(url: string): string | undefined {
  const path = url.split('?')[0] ?? url;
  const segments = path.split('/').filter(Boolean);
  const last = segments.at(-1);
  if (!last || last === 'use-session' || last === 'restore-session' || last === 'redeem' || last === 'complete' || last === 'convert') {
    return segments.at(-2);
  }
  return last;
}

export function createQueueOps(
  storage: OfflineStorage,
  deps: { getLastSyncAt: () => Promise<string | null>; matchRoute: RouteMatcher['matchRoute'] },
) {
  const listeners = new Set<(status: SyncStatus) => void>();
  let syncingFlag = false;
  let lastSyncError: string | null = null;

  async function notifyListeners(): Promise<void> {
    const status: SyncStatus = {
      pendingCount: await storage.countQueueItems(),
      lastSyncAt: await deps.getLastSyncAt(),
      isSyncing: syncingFlag,
      lastSyncError,
    };
    listeners.forEach((l) => l(status));
  }

  function subscribeSyncStatus(listener: (status: SyncStatus) => void): () => void {
    listeners.add(listener);
    void notifyListeners();
    return () => listeners.delete(listener);
  }

  async function getSyncStatus(): Promise<SyncStatus> {
    return {
      pendingCount: await storage.countQueueItems(),
      lastSyncAt: await deps.getLastSyncAt(),
      isSyncing: false,
      lastSyncError: null,
    };
  }

  function setSyncing(isSyncing: boolean, error: string | null = null): void {
    syncingFlag = isSyncing;
    lastSyncError = error;
    void notifyListeners();
  }

  async function enqueue(
    item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'retries'>,
  ): Promise<SyncQueueItem> {
    const entry: SyncQueueItem = {
      ...item,
      id: generateTempId(),
      createdAt: nowIso(),
      retries: 0,
    };
    await storage.addQueueItem(entry);
    await notifyListeners();
    return entry;
  }

  async function dequeue(id: string): Promise<void> {
    await storage.deleteQueueItem(id);
    await notifyListeners();
  }

  async function getQueue(): Promise<SyncQueueItem[]> {
    const items = await storage.listQueueItems();
    return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async function getPendingDeleteIds(entityType: EntityType): Promise<Set<string>> {
    const queue = await getQueue();
    const ids = new Set<string>();

    for (const item of queue) {
      if (item.method !== 'DELETE' || item.entityType !== entityType) continue;
      if (item.entityId) ids.add(item.entityId);
      const idFromUrl = entityIdFromUrl(item.url);
      if (idFromUrl) ids.add(idFromUrl);
    }

    return ids;
  }

  async function hasPendingChangesForUrl(
    url: string,
    params?: Record<string, unknown>,
  ): Promise<boolean> {
    const route = deps.matchRoute('GET', url);
    if (!route) return false;

    const queue = await getQueue();
    if (queue.length === 0) return false;

    for (const item of queue) {
      if (item.entityType !== route.entity) continue;

      if (route.operation === 'list') {
        const scopedClientId = route.clientId ?? (params?.clientId as string | undefined);
        if (scopedClientId) {
          if (clientIdFromUrl(item.url) === scopedClientId) return true;
          continue;
        }
        return true;
      }

      if (route.operation === 'get') {
        if (route.id) {
          if (item.entityId === route.id || item.url.includes(`/${route.id}`)) return true;
        }
        if (route.clientId && clientIdFromUrl(item.url) === route.clientId) return true;
      }
    }

    return false;
  }

  async function incrementRetries(id: string): Promise<void> {
    const item = await storage.getQueueItem(id);
    if (item) {
      await storage.updateQueueItem({ ...item, retries: item.retries + 1 });
    }
  }

  async function clearQueue(): Promise<void> {
    await storage.clearQueueItems();
    await notifyListeners();
  }

  async function saveIdMapping(
    tempId: string,
    realId: string,
    entityType: SyncQueueItem['entityType'],
  ): Promise<void> {
    await storage.putIdMapping({ tempId, realId, entityType });
  }

  async function resolveId(id: string): Promise<string> {
    const mapping = await storage.getIdMapping(id);
    return mapping?.realId ?? id;
  }

  async function resolvePayload(data: unknown): Promise<unknown> {
    if (data === null || data === undefined) return data;
    if (typeof data !== 'object') return data;
    if (Array.isArray(data)) {
      return Promise.all(data.map((item) => resolvePayload(item)));
    }

    const obj = { ...(data as Record<string, unknown>) };
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string' && value.startsWith(TEMP_ID_PREFIX)) {
        obj[key] = await resolveId(value);
      } else if (typeof value === 'object') {
        obj[key] = await resolvePayload(value);
      }
    }
    return obj;
  }

  async function resolveUrl(url: string): Promise<string> {
    const parts = url.split('/');
    const resolved = await Promise.all(
      parts.map(async (part) => (part.startsWith(TEMP_ID_PREFIX) ? resolveId(part) : part)),
    );
    return resolved.join('/');
  }

  return {
    subscribeSyncStatus,
    getSyncStatus,
    setSyncing,
    enqueue,
    dequeue,
    getQueue,
    getPendingDeleteIds,
    hasPendingChangesForUrl,
    incrementRetries,
    clearQueue,
    saveIdMapping,
    resolveId,
    resolvePayload,
    resolveUrl,
  };
}

export type QueueOps = ReturnType<typeof createQueueOps>;
